import { ref, shallowRef, computed } from 'vue';
import { MapAreaService, MapRenderService } from '@/modules/map-editor';
import type { CanvasContexts, RenderDataGetters } from '@/modules/map-editor';

/**
 * Composable for managing canvas elements and rendering.
 * Owns the three canvas layers (main, buffered, highlights), the render service, and the animation loop.
 */
export function useCanvasManager(mapAreaService: MapAreaService) {
  const canvasMain = ref<HTMLCanvasElement | null>(null);
  const canvasBuffered = ref<HTMLCanvasElement | null>(null);
  const canvasHighlights = ref<HTMLCanvasElement | null>(null);

  const ctx = ref<CanvasRenderingContext2D | null>(null);
  const ctxBuffered = ref<CanvasRenderingContext2D | null>(null);
  const ctxHighlights = ref<CanvasRenderingContext2D | null>(null);

  const contexts = computed<CanvasContexts>(() => ({
    ctx: ctx.value,
    ctxBuffered: ctxBuffered.value,
    ctxHighlights: ctxHighlights.value
  }));

  const pixelRatio = ref(window.devicePixelRatio);

  // Initialize render service — shallowRef preserves MapRenderService private fields in the type
  const renderService = shallowRef<MapRenderService | null>(null);

  // Animation frame
  let animationFrameId: number | null = null;
  let idleFrameCount = 0;
  const IDLE_FRAMES_BEFORE_PAUSE = 5;

  /**
   * Register data-supplier closures on the render service.
   *
   * @param getters - Closures that return nodes, edges, zoom, etc.
   */
  function setDataGetters(getters: RenderDataGetters): void {
    const service = renderService.value;
    if (!service) {
      console.error('Render service not initialized');
      return;
    }

    service.setDataGetters(getters);
  }

  /**
   * Initialize offscreen (buffered) canvas with dimensions matching the main canvas.
   */
  function initOffscreenCanvas(): void {
    if (!canvasMain.value) {
      console.error('Main canvas is not available');
      return;
    }

    canvasBuffered.value = document.createElement('canvas');
    canvasBuffered.value.width = canvasMain.value.clientWidth * pixelRatio.value;
    canvasBuffered.value.height = canvasMain.value.clientHeight * pixelRatio.value;

    ctxBuffered.value = canvasBuffered.value.getContext('2d');
  }

  function setCanvasSize(context: CanvasRenderingContext2D | null): void {
    if (!context || !canvasMain.value) return;

    context.canvas.width = canvasMain.value.clientWidth * pixelRatio.value;
    context.canvas.height = canvasMain.value.clientHeight * pixelRatio.value;
  }

  /**
   * Set background color on the main canvas element.
   *
   * @param bgColor - CSS color string
   */
  function setCanvasBackgroundColor(bgColor: string): void {
    if (!canvasMain.value?.parentElement) return;
    // Apply background on the parent element rather than the <canvas> itself.
    // Browsers may skip repainting CSS background-image (gradients) on a canvas
    // that is being actively drawn to, while background-color (solids) works fine.
    // A regular div doesn't have this GPU-compositing quirk.
    canvasMain.value.parentElement.style.background = bgColor;
  }

  /**
   * Resize all three canvas layers to match the main canvas client dimensions.
   */
  function resizeAllCanvases(): void {
    setCanvasSize(ctx.value);
    setCanvasSize(ctxBuffered.value);
    setCanvasSize(ctxHighlights.value);
  }

  /**
   * Re-read `window.devicePixelRatio` (e.g. after moving to a different monitor).
   */
  function updatePixelRatio(): void {
    pixelRatio.value = window.devicePixelRatio;
  }

  /**
   * Initialize 2D contexts for the main and highlights canvases and create the render service.
   */
  function initContexts(): void {
    if (!canvasMain.value || !canvasHighlights.value || !canvasBuffered.value) {
      console.error('Canvas elements are not available');
      return;
    }

    ctx.value = canvasMain.value.getContext('2d');
    ctxHighlights.value = canvasHighlights.value.getContext('2d');

    // Initialize render service
    renderService.value = new MapRenderService(contexts.value, mapAreaService);

    // Register wake callback so the idle-paused loop restarts on dirty flags
    mapAreaService.setWakeCallback(wakeAnimation);
  }

  /**
   * Core animation loop — delegates to `MapRenderService.frameUpdate()` each frame.
   * Pauses after consecutive idle frames to save CPU/battery.
   */
  function animate(): void {
    const service = renderService.value;
    if (!service) {
      animationFrameId = requestAnimationFrame(animate);
      return;
    }

    try {
      const rendered = service.frameUpdate(mapAreaService.getState());
      if (rendered) {
        idleFrameCount = 0;
      } else {
        idleFrameCount++;
      }
    } catch (error) {
      console.error('Animation loop error:', error);
    }

    if (idleFrameCount >= IDLE_FRAMES_BEFORE_PAUSE) {
      animationFrameId = null;
      return;
    }

    animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Start the animation loop (idempotent).
   */
  function startAnimation(): void {
    if (animationFrameId) return;
    animate();
  }

  /**
   * Resume the animation loop if it paused due to idle frames.
   * Called by MapAreaService when dirty flags are set.
   */
  function wakeAnimation(): void {
    idleFrameCount = 0;
    if (!animationFrameId && renderService.value) {
      animate();
    }
  }

  /**
   * Stop the animation loop.
   */
  function stopAnimation(): void {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  }

  /**
   * Tear down all canvas state and stop the animation loop.
   */
  function cleanup(): void {
    stopAnimation();
    mapAreaService.setWakeCallback(null);
    renderService.value = null;
    canvasBuffered.value = null;
    ctx.value = null;
    ctxBuffered.value = null;
    ctxHighlights.value = null;
  }

  return {
    canvasMain,
    canvasHighlights,
    contexts,
    pixelRatio,
    initOffscreenCanvas,
    initContexts,
    setCanvasBackgroundColor,
    resizeAllCanvases,
    updatePixelRatio,
    setDataGetters,
    startAnimation,
    stopAnimation,
    wakeAnimation,
    cleanup
  };
}